import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { BiSend, BiSmile, BiCommand } from 'react-icons/bi';
import { useAuth } from '../context/AuthContext.js';
import { collection, addDoc, query, orderBy, limit, onSnapshot, serverTimestamp, doc, getDoc, updateDoc, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase.js';
import { recordDnaInteraction } from '../services/dnaProfileService';
import { DNA_INTERACTIONS } from '../services/dnaProfileService';
import './StreamChat.css';

const ChatMessage = ({ username, message, timestamp, isSystem, isDnaCommand }) => (
  <div className={`py-2 px-4 hover:bg-gray-800/50 rounded-lg transition-colors ${isSystem ? 'bg-purple-900/20' : ''} ${isDnaCommand ? 'bg-blue-900/20' : ''}`}>
    <div className="flex items-start space-x-2">
      <div className="flex-1">
        <div className="flex items-baseline space-x-2">
          <span className={`font-medium ${isSystem ? 'text-purple-400' : 'text-blue-400'}`}>{username}</span>
          <span className="text-xs text-gray-500">{new Date(timestamp?.toDate()).toLocaleTimeString()}</span>
        </div>
        <p className="text-gray-300 mt-1">{message}</p>
      </div>
    </div>
  </div>
);

const StreamChat = ({ streamId, isLive }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCommands, setShowCommands] = useState(false);
  const chatContainerRef = useRef(null);
  const chatEndRef = useRef(null);

  const regularCommands = [
    { command: '!funny', description: 'Vote for entertaining content' },
    { command: '!rage', description: 'Vote for intense reactions' },
    { command: '!clutch', description: 'Vote for skilled plays' },
    { command: '!leader', description: 'Vote for strategic gameplay' },
    { command: '!unhinged', description: 'Vote for wild behavior' },
    { command: '!wild', description: 'Vote for unpredictable moments' },
    { command: '!boring', description: 'Vote for dull content' },
    { command: '!troll', description: 'Vote for mischievous behavior' },
    { command: '!unbeatable', description: 'Vote for dominant performance' },
    { command: '!toxic', description: 'Vote for negative behavior' },
    { command: '!calm', description: 'Vote for composed behavior' }
  ];

  useEffect(() => {
    if (!streamId || !isLive) {
      setIsLoading(false);
      setMessages([]);
      return;
    }

    try {
      const chatRef = collection(db, 'streams', streamId, 'chat');
      const q = query(chatRef, orderBy('timestamp', 'desc'), limit(50));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const newMessages = [];
        snapshot.forEach((doc) => {
          newMessages.unshift({ id: doc.id, ...doc.data() });
        });
        setMessages(newMessages);
        setIsLoading(false);
      }, (error) => {
        console.error('Error fetching chat messages:', error);
        setError('Failed to load chat messages');
        setIsLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up chat listener:', error);
      setError('Failed to initialize chat');
      setIsLoading(false);
    }
  }, [streamId, isLive]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || !streamId) return;

    try {
      const messageText = newMessage.trim();
      const isDnaCommand = messageText.startsWith('!') && Object.values(DNA_INTERACTIONS)
        .some(type => messageText.toLowerCase().startsWith(`!${type}`));

      if (isDnaCommand) {
        const commandType = messageText.substring(1).split(' ')[0].toLowerCase();
        await recordDnaInteraction(streamId, currentUser.uid, commandType);
      }

      const chatRef = collection(db, 'streams', streamId, 'chat');
      await addDoc(chatRef, {
        text: messageText,
        userId: currentUser.uid,
        username: currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous',
        timestamp: serverTimestamp(),
        isSystem: false,
        isDnaCommand
      });

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleCommandClick = (command) => {
    setNewMessage(command);
    setShowCommands(false);
  };

  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-700 rounded w-3/4"></div>
          <div className="h-4 bg-gray-700 rounded"></div>
          <div className="h-4 bg-gray-700 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Stream Chat</h2>
        <button
          onClick={() => setShowCommands(!showCommands)}
          className="text-gray-400 hover:text-white"
        >
          <BiCommand className="w-5 h-5" />
        </button>
      </div>

      {showCommands && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mb-4 bg-gray-900 rounded-lg p-4"
        >
          <h3 className="text-sm font-medium text-gray-400 mb-2">Available Commands</h3>
          <div className="space-y-2">
            {regularCommands.map(({ command, description }) => (
              <button
                key={command}
                onClick={() => handleCommandClick(command)}
                className="block w-full text-left px-3 py-2 rounded hover:bg-gray-800"
              >
                <span className="text-purple-400">{command}</span>
                <span className="text-gray-500 text-sm ml-2">{description}</span>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      <div
        ref={chatContainerRef}
        className="h-96 overflow-y-auto mb-4 space-y-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900"
      >
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            username={message.username}
            message={message.text}
            timestamp={message.timestamp}
            isSystem={message.isSystem}
            isDnaCommand={message.isDnaCommand}
          />
        ))}
        <div ref={chatEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="flex space-x-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isLive ? "Send a message..." : "Stream is offline"}
            disabled={!isLive}
            className="w-full bg-gray-700 text-white px-4 py-2 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button
            type="button"
            onClick={() => setShowCommands(!showCommands)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          >
            <BiSmile className="w-5 h-5" />
          </button>
        </div>
        <button
          type="submit"
          disabled={!isLive || !newMessage.trim()}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <BiSend className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
};

export default StreamChat; 